import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getFechaOperativaSnapshot,
  setFechaOperativaSnapshot,
  FECHA_OPERATIVA_STORAGE_PREFIX,
} from '../utils/fechaOperativaStore';

const STORAGE_PREFIX = FECHA_OPERATIVA_STORAGE_PREFIX;

type FechaOperativaContextValue = {
  /** YYYY-MM-DD cuando hay override; null = reloj real */
  fechaYmd: string | null;
  /** HH:mm opcional */
  hora: string | null;
  isOverrideActive: boolean;
  setOverride: (ymd: string, hora?: string | null) => void;
  clearOverride: () => void;
  /** Pedido desde menú usuario para abrir el popover del chip */
  openEditorRequestId: number;
  requestOpenEditor: () => void;
};

const FechaOperativaContext = createContext<FechaOperativaContextValue | null>(null);

function storageKey(tenantId: number | string | null | undefined): string {
  return `${STORAGE_PREFIX}.${tenantId ?? 'default'}`;
}

function readStored(tenantId: number | string | null | undefined): {
  ymd: string | null;
  hora: string | null;
} {
  try {
    const raw = sessionStorage.getItem(storageKey(tenantId));
    if (!raw) return { ymd: null, hora: null };
    const parsed = JSON.parse(raw) as { ymd?: string; hora?: string | null };
    if (!parsed.ymd || !/^\d{4}-\d{2}-\d{2}$/.test(parsed.ymd)) {
      return { ymd: null, hora: null };
    }
    const hora =
      parsed.hora && /^\d{2}:\d{2}$/.test(parsed.hora) ? parsed.hora : null;
    return { ymd: parsed.ymd, hora };
  } catch {
    return { ymd: null, hora: null };
  }
}

function writeStored(
  tenantId: number | string | null | undefined,
  ymd: string | null,
  hora: string | null
): void {
  const key = storageKey(tenantId);
  if (!ymd) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify({ ymd, hora }));
}

export const FechaOperativaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, tenantId } = useAuth();
  const [fechaYmd, setFechaYmd] = useState<string | null>(
    () => readStored(tenantId).ymd
  );
  const [hora, setHora] = useState<string | null>(
    () => readStored(tenantId).hora
  );
  const [openEditorRequestId, setOpenEditorRequestId] = useState(0);

  // Snapshot sincronizado en render (antes que los hijos) para que
  // useState(() => toLocalDateStringYMD()) vea el override guardado.
  // No usar solo useEffect: corre después del primer commit de los forms.
  // Mantener el snapshot aunque user aún sea null tras F5 (Auth hidrata después).
  setFechaOperativaSnapshot({ ymd: fechaYmd, hora });

  useEffect(() => {
    if (!user) {
      // No limpiar sessionStorage acá: tras F5 user es null un instante y
      // borraría la fecha de la pestaña. La limpieza es solo en logout.
      return;
    }
    const stored = readStored(tenantId);
    setFechaYmd(stored.ymd);
    setHora(stored.hora);
  }, [user, tenantId]);

  const setOverride = useCallback(
    (ymd: string, horaNext?: string | null) => {
      const trimmed = ymd.trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return;
      const h =
        horaNext && /^\d{2}:\d{2}$/.test(horaNext.trim())
          ? horaNext.trim()
          : null;
      setFechaYmd(trimmed);
      setHora(h);
      writeStored(tenantId, trimmed, h);
      setFechaOperativaSnapshot({ ymd: trimmed, hora: h });
    },
    [tenantId]
  );

  const clearOverride = useCallback(() => {
    setFechaYmd(null);
    setHora(null);
    writeStored(tenantId, null, null);
    setFechaOperativaSnapshot({ ymd: null, hora: null });
  }, [tenantId]);

  const requestOpenEditor = useCallback(() => {
    setOpenEditorRequestId((n) => n + 1);
  }, []);

  const value = useMemo<FechaOperativaContextValue>(
    () => ({
      fechaYmd,
      hora,
      isOverrideActive: Boolean(fechaYmd),
      setOverride,
      clearOverride,
      openEditorRequestId,
      requestOpenEditor,
    }),
    [
      fechaYmd,
      hora,
      setOverride,
      clearOverride,
      openEditorRequestId,
      requestOpenEditor,
    ]
  );

  return (
    <FechaOperativaContext.Provider value={value}>
      {children}
    </FechaOperativaContext.Provider>
  );
};

const defaultValue: FechaOperativaContextValue = {
  fechaYmd: null,
  hora: null,
  isOverrideActive: false,
  setOverride: () => {},
  clearOverride: () => {},
  openEditorRequestId: 0,
  requestOpenEditor: () => {},
};

export function useFechaOperativa(): FechaOperativaContextValue {
  return useContext(FechaOperativaContext) ?? defaultValue;
}

/** Re-export por si algún código necesita leer el bridge fuera de React. */
export { getFechaOperativaSnapshot };
